import { getTicketsBaseUrl } from '../helpers/util';

/**
 * Per-event registration form (`r`) for creatives share / referral tracking.
 * Host (stage vs prod) is chosen from the current hostname via getTicketsBaseUrl().
 * Path uses /e/reconnect_{eventCode}.
 *
 * utm_source: "{name} {company}" joined with a space (e.g. "testing new_test").
 * URL encoding turns that space into "+" → readable "testing+new_test" (not %2B).
 * Tickets: decode, then split on the first space → name / company.
 */
const EVENT_REFERRAL_FORM_VALUES = {
  '9': 'wtemiami2026-trade_visitor',
  '27': 'bltm2026-trade_visitor',
  '28': 'wtemiami2026-trade_visitor',
};

const normalizeEventCode = (eventCode) =>
  String(eventCode || '').trim().replace(/^reconnect_/i, '');

/** Sanitize a name/company fragment (spaces inside a field → _). */
export const slugifyReferralPart = (value = '') =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

/**
 * Build utm_source from name + company joined by a space.
 * Example: name "testing", company "new test" → "testing new_test"
 * (in the URL: testing+new_test)
 */
export const buildReferralUtmSource = (name = '', company = '') => {
  const nameSlug = slugifyReferralPart(name);
  const companySlug = slugifyReferralPart(company);
  if (nameSlug && companySlug) return `${nameSlug} ${companySlug}`;
  if (nameSlug) return nameSlug;
  if (companySlug) return companySlug;
  return 'anonymous';
};

/**
 * Registration base URL for an event (env-aware host + reconnect path + form).
 * @returns {string|null}
 */
export const getEventRegistrationUrl = (eventCode) => {
  const code = normalizeEventCode(eventCode);
  if (!code) return null;

  const formValue = EVENT_REFERRAL_FORM_VALUES[code];
  if (!formValue) return null;

  return `${getTicketsBaseUrl()}/e/reconnect_${code}?r=${formValue}`;
};

/**
 * Registration link with utm_source for referral / royalty tracking.
 * @returns {string|null}
 */
export const buildCreativesReferralUrl = (eventCode, name = '', company = '') => {
  const base = getEventRegistrationUrl(eventCode);
  if (!base) return null;

  try {
    const url = new URL(base);
    url.searchParams.set('utm_source', buildReferralUtmSource(name, company));
    return url.toString();
  } catch {
    return null;
  }
};

/**
 * Clean, branded frontend referral URL without UTM parameters inline.
 * Example: https://wtemiami.com/register?ref=testing_new_test
 * @returns {string}
 */
export const buildFrontendReferralUrl = (name = '', company = '') => {
  const nameSlug = slugifyReferralPart(name);
  const companySlug = slugifyReferralPart(company);
  if (!nameSlug && !companySlug) return null;

  const baseUrl = window.location.origin;
  const ref = buildReferralUtmSource(name, company).replace(/\s+/g, '_');
  return `${baseUrl}/?ref=${ref}`;
};

/**
 * Full referral URL based on email.
 * Example (stage): https://staging-tickets.fairfest.in/e/reconnect_9?r=wtemiami2026-trade_visitor&utm_source=ref_<base64url_email>
 * @returns {string|null}
 */
export const buildFullReferralUrlByEmail = (eventCode, email) => {
  if (!email) return null;
  const base = getEventRegistrationUrl(eventCode);
  if (!base) return null;

  try {
    const url = new URL(base);
    const encodedEmail = btoa(email.trim())
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

    url.searchParams.set('utm_source', `ref_${encodedEmail}`);
    return url.toString();
  } catch {
    return null;
  }
};
